import { Anchor, AppShell, Breadcrumbs, Button, Group, Image, Stack, Text } from "@mantine/core";
import { Link } from "react-router-dom";

import { useIAMAuth } from "@/auth/iam";
import { useLabLook } from "@/branding/PortalTheme";
import { t } from "@/i18n";

/**
 * Shared authenticated shell: a header (app title → home, signed-in identity,
 * sign-out) + a breadcrumb trail + the page body. Replaces the per-page
 * `<AppShell>` boilerplate and adds the previously-missing sign-out affordance.
 *
 * Reads auth from the module store (`useIAMAuth` → `useSyncExternalStore`), so it
 * renders without an `<AuthProvider>` wrapper — unauthenticated just hides the
 * identity + no-ops sign-out.
 */

export interface Crumb {
  label: string;
  /** A link target; the trailing (current-page) crumb omits it. */
  to?: string;
}

interface AppLayoutProps {
  breadcrumbs?: Crumb[];
  children: React.ReactNode;
}

export function AppLayout({ breadcrumbs, children }: AppLayoutProps): JSX.Element {
  const { user, signOut } = useIAMAuth();
  const identity = user !== null ? (user.name ?? user.email ?? user.sub) : null;
  // Gate B white-labelling (D4): a lab user sees the lab's name and logo here, Medicus staff
  // and an unbranded lab the app's own title. The doctor's review page shares this header.
  const look = useLabLook();

  return (
    <AppShell header={{ height: 56 }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Anchor component={Link} to="/" fw={600} underline="never" c="inherit">
            {look !== null && (look.logo !== null || look.displayName !== null) ? (
              <Group gap="sm" wrap="nowrap" data-testid="lab-header">
                {look.logo !== null && (
                  <Image
                    src={look.logo.dataUri}
                    alt={look.displayName ?? t("branding.header_logo_alt")}
                    h={32}
                    w="auto"
                    fit="contain"
                  />
                )}
                {look.displayName !== null && <span>{look.displayName}</span>}
              </Group>
            ) : (
              t("app.title")
            )}
          </Anchor>
          <Group gap="sm" wrap="nowrap">
            {identity !== null && (
              <Text size="sm" c="dimmed">
                {identity}
              </Text>
            )}
            <Button variant="subtle" size="xs" onClick={() => void signOut()}>
              {t("app.sign_out")}
            </Button>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Main>
        <Stack gap="md" p="lg">
          {breadcrumbs !== undefined && breadcrumbs.length > 0 && (
            <Breadcrumbs>
              {breadcrumbs.map((c) =>
                c.to !== undefined ? (
                  <Anchor key={c.label} component={Link} to={c.to} size="sm">
                    {c.label}
                  </Anchor>
                ) : (
                  <Text key={c.label} size="sm" c="dimmed">
                    {c.label}
                  </Text>
                ),
              )}
            </Breadcrumbs>
          )}
          {children}
        </Stack>
      </AppShell.Main>
    </AppShell>
  );
}
